import { useState, useEffect } from 'react'
import { Modal } from '@/shared/components/Modal'
import { api, getErrorMessage } from '@/shared/api/generated'
import type { MemberResponse } from '@/shared/api/generated'

interface GroupInfoDialogProps {
  groupId: number
  groupName: string
  onClose: () => void
}

export function GroupInfoDialog({ groupId, groupName, onClose }: GroupInfoDialogProps) {
  const [members, setMembers] = useState<MemberResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.listGroupMembers(groupId)
      .then(setMembers)
      .catch(err => setError(getErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [groupId])

  const admins = members.filter(m => m.role === 'admin')
  const others = members.filter(m => m.role !== 'admin')

  return (
    <Modal title={`Group: ${groupName}`} onClose={onClose} wide>
      {error && <div className="alert error">{error}</div>}
      {loading ? <div className="empty-state">Loading…</div> : (
        <div className="info-dialog">
          <section>
            <h4>Admins</h4>
            {admins.length === 0 ? <p className="muted">No admins</p> : (
              <ul className="info-member-list">
                {admins.map(m => <li key={m.user_id}>{m.username}</li>)}
              </ul>
            )}
          </section>
          <section>
            <h4>Members ({others.length})</h4>
            {others.length === 0 ? <p className="muted">No other members</p> : (
              <ul className="info-member-list">
                {others.map(m => <li key={m.user_id}>{m.username}</li>)}
              </ul>
            )}
          </section>
        </div>
      )}
    </Modal>
  )
}

interface ViewInfoDialogProps {
  viewId: number
  onClose: () => void
}

export function ViewInfoDialog({ viewId, onClose }: ViewInfoDialogProps) {
  const [admins, setAdmins] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.getViewInfo(viewId)
      .then(info => setAdmins(info.group_admins))
      .catch(err => setError(getErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [viewId])

  return (
    <Modal title="View Info" onClose={onClose}>
      {error && <div className="alert error">{error}</div>}
      {loading ? <div className="empty-state">Loading…</div> : (
        <div className="info-dialog">
          <section>
            <h4>Group Admins</h4>
            {admins.length === 0 ? <p className="muted">No admins</p> : (
              <ul className="info-member-list">
                {admins.map(name => <li key={name}>{name}</li>)}
              </ul>
            )}
          </section>
        </div>
      )}
    </Modal>
  )
}
